import { useCallback, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { intl } from '../../../i18n'
import { colorPalette } from '../../style/colorPalette'
import ProcessScopeSearchSelectPopper from '../ProcessScopeSearchSelectPopper/ProcessScopeSearchSelectPopper'
import { useMilestoneTargetOptions } from './useMilestoneTargetOptions'
import { UNRESOLVED_TARGET_LABEL } from './model'

/**
 * 「全体」(対象未設定 = プロジェクト全体)を表す擬似オプションの値。
 * 親を持たないため buildTree でルート扱いになり、先頭に置けば先頭行に並ぶ。
 */
const WHOLE_PROJECT_VALUE = '__whole_project__'

type MilestoneTargetSelectProps = {
  value: string | undefined
  onChange: (value: string | undefined) => void
}

export const MilestoneTargetSelect = ({
  value,
  onChange,
}: MilestoneTargetSelectProps) => {
  const {
    options: wbsItemOptions,
    parentMap,
    isLoading,
  } = useMilestoneTargetOptions()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const options = useMemo(
    () => [
      {
        value: WHOLE_PROJECT_VALUE,
        name: intl.formatMessage({ id: 'whole.project' }),
      },
      ...wbsItemOptions,
    ],
    [wbsItemOptions]
  )

  const selectedValue = value ?? WHOLE_PROJECT_VALUE
  const selectedValues = useMemo(
    () => new Set([selectedValue]),
    [selectedValue]
  )
  const selectedOption = useMemo(
    () => options.find(option => option.value === selectedValue),
    [options, selectedValue]
  )
  const targetLabel = value
    ? (selectedOption?.name ?? UNRESOLVED_TARGET_LABEL)
    : intl.formatMessage({ id: 'whole.project' })

  const handleToggleItem = useCallback(
    (itemValue: string) => {
      onChange(itemValue === WHOLE_PROJECT_VALUE ? undefined : itemValue)
      setOpen(false)
    },
    [onChange]
  )

  const handleClose = useCallback(() => setOpen(false), [])

  // Popper を開いている間は ClickAwayListener も閉じる側に働くため、関数形式の
  // setState でトグルすると閉じた直後に開き直してしまう。描画時点の open で分岐する。
  const handleFieldClick = useCallback(() => setOpen(!open), [open])

  return (
    <>
      <TargetField ref={anchorRef} onClick={handleFieldClick}>
        {selectedOption?.iconUrl && <TargetIcon src={selectedOption.iconUrl} />}
        <TargetName>{targetLabel}</TargetName>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </TargetField>
      {open && (
        <ProcessScopeSearchSelectPopper
          open={open}
          anchorEl={anchorRef.current ?? undefined}
          options={options}
          parentMap={parentMap}
          isLoading={isLoading}
          selectedValues={selectedValues}
          onToggleItem={handleToggleItem}
          onClose={handleClose}
          singleSelect={true}
          searchPlaceholder={intl.formatMessage({
            id: 'milestone.field.target.searchPlaceholder',
          })}
        />
      )}
    </>
  )
}

const TargetField = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: 320,
  height: 35,
  padding: '0 8px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: 4,
  cursor: 'pointer',
  boxSizing: 'border-box',
  '&:hover': {
    borderColor: colorPalette.skyBlue[7],
  },
})

const TargetIcon = styled('img')({
  width: 16,
  height: 16,
  flexShrink: 0,
})

const TargetName = styled('span')({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: colorPalette.monotone[9],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const ExpandMoreIcon = styled(ExpandMoreRounded)({
  fontSize: 16,
  color: colorPalette.monotone[4],
  flexShrink: 0,
})

const ExpandLessIcon = styled(ExpandLessRounded)({
  fontSize: 16,
  color: colorPalette.monotone[4],
  flexShrink: 0,
})
